import type { LatLng } from '@geosport/shared';
import type { MultiPolygon, Polygon, Position } from 'geojson';

/**
 * Country / US-state polygons (Natural Earth 1:110m, public domain), used to highlight the
 * answer's region on the reveal. ~17 KB gzipped, fetched once on the first reveal.
 */
export interface Region {
  name: string;
  code: string;
  kind: 'country' | 'state';
  geometry: Polygon | MultiPolygon;
}

interface RawFeature {
  properties: { n: string; c: string; k: 'country' | 'state' };
  geometry: Polygon | MultiPolygon;
}

let cached: Promise<Region[]> | null = null;

export function loadRegions(): Promise<Region[]> {
  if (!cached) {
    cached = fetch('/regions.json')
      .then((res) => {
        if (!res.ok) throw new Error(`regions: ${res.status}`);
        return res.json() as Promise<{ features: RawFeature[] }>;
      })
      .then((fc) =>
        fc.features.map((f) => ({
          name: f.properties.n,
          code: f.properties.c,
          kind: f.properties.k,
          geometry: f.geometry,
        })),
      )
      .catch((err: unknown) => {
        cached = null;
        throw err;
      });
  }
  return cached;
}

/** Ray-casting point-in-ring test on [lng, lat] pairs. */
function inRing(ring: Position[], lng: number, lat: number): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi = 0, yi = 0] = ring[i] ?? [];
    const [xj = 0, yj = 0] = ring[j] ?? [];
    const crosses = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

function inPolygon(rings: Position[][], lng: number, lat: number): boolean {
  const [outer, ...holes] = rings;
  if (!outer || !inRing(outer, lng, lat)) return false;
  return !holes.some((h) => inRing(h, lng, lat));
}

export function containsPoint(geometry: Polygon | MultiPolygon, p: LatLng): boolean {
  const lng = ((p.lng + 540) % 360) - 180;
  if (geometry.type === 'Polygon') return inPolygon(geometry.coordinates, lng, p.lat);
  return geometry.coordinates.some((poly) => inPolygon(poly, lng, p.lat));
}

/** The most specific region containing a point: a US state before its country. */
export async function findRegion(p: LatLng): Promise<Region | null> {
  try {
    const regions = await loadRegions();
    return regions.find((r) => containsPoint(r.geometry, p)) ?? null;
  } catch {
    return null;
  }
}
